import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Plus, Loader2, ArrowLeft, Trash2, Send, Lock, CornerDownRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { GardienBadge } from "@/components/GardienBadge";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";

const CATEGORIES = ["Conseils", "Témoignages", "Questions"];
const catColor = {
  Conseils: "bg-primary/15 text-primary",
  "Témoignages": "bg-secondary/25 text-secondary-foreground",
  Questions: "bg-warm/20 text-warm",
};
const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-colors";
const spaceLabel = { soignant: "Soignant", famille: "Famille", admin: "Équipe" };

const fmt = (d) => { try { return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" }); } catch { return ""; } };

const Forum = () => {
  const { admin, setConnexionOpen, connexionOpen } = useApp();
  const [authed, setAuthed] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [nt, setNt] = useState({ title: "", category: "Conseils", body: "" });
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null); // {thread, posts}
  const [reply, setReply] = useState("");

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/forum/threads");
      setThreads(data); setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) setAuthed(false);
    }
    setLoading(false);
  };
  useEffect(() => { loadThreads(); }, []);
  // Recharge quand la fenêtre de connexion se ferme (l'utilisateur vient peut-être de se connecter)
  useEffect(() => { if (!connexionOpen && authed === false) loadThreads(); }, [connexionOpen]); // eslint-disable-line

  const shown = filter === "Tous" ? threads : threads.filter((t) => t.category === filter);

  const createThread = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/forum/threads", nt);
      toast.success("Votre sujet a été publié");
      setNt({ title: "", category: "Conseils", body: "" }); setShowNew(false);
      loadThreads();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  const openThread = async (id) => {
    try { const { data } = await api.get(`/forum/threads/${id}`); setActive(data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const sendReply = async (e) => {
    e.preventDefault(); if (!reply.trim()) return; setBusy(true);
    try {
      await api.post(`/forum/threads/${active.thread.id}/posts`, { body: reply });
      setReply("");
      const { data } = await api.get(`/forum/threads/${active.thread.id}`);
      setActive(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy(false);
  };

  const delThread = async (id) => {
    try { await api.delete(`/forum/threads/${id}`); toast.success("Sujet supprimé"); setActive(null); loadThreads(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const delPost = async (pid) => {
    try {
      await api.delete(`/forum/posts/${pid}`); toast.success("Message supprimé");
      const { data } = await api.get(`/forum/threads/${active.thread.id}`); setActive(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  // ---- Gate: non connecté ----
  if (authed === false) {
    return (
      <div className="pt-32 pb-20 min-h-screen">
        <div className="max-w-xl mx-auto px-6 text-center" data-testid="forum-gate">
          <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center mx-auto"><Lock className="w-7 h-7 text-primary" strokeWidth={1.4} /></div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-light mt-6">Forum / Échanges</h1>
          <p className="mt-5 text-foreground/70 leading-relaxed">
            Un espace bienveillant réservé aux familles et aux soignants pour partager conseils, témoignages et
            questions. Connectez-vous pour rejoindre la conversation.
          </p>
          <button onClick={() => setConnexionOpen(true)} data-testid="forum-connect-btn" className="mt-8 rounded-full bg-primary text-primary-foreground px-9 py-4 hover:scale-105 transition-transform duration-300">
            Se connecter pour participer
          </button>
          <div className="mt-10 flex justify-center">
            <GardienBadge gardienId="rose-auguste" label="Gardiens de ce forum" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 min-h-screen">
      <section className="max-w-4xl mx-auto px-6 lg:px-10">
        {!active ? (
          <>
            <Reveal>
              <p className="text-sm uppercase tracking-[0.3em] text-primary mb-4">Espace communautaire</p>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <h1 className="font-serif-display text-5xl sm:text-6xl font-light">Forum / Échanges</h1>
                <button onClick={() => setShowNew((v) => !v)} data-testid="new-thread-btn" className="rounded-full bg-primary text-primary-foreground px-6 py-3 flex items-center gap-2 hover:scale-105 transition-transform duration-300">
                  <Plus className="w-4 h-4" /> Nouveau sujet
                </button>
              </div>
              <p className="mt-5 text-foreground/70 max-w-2xl leading-relaxed">
                Partagez avec bienveillance vos expériences, vos astuces et vos questions. Ensemble, on avance en douceur.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="forum-banner">
                <img src="/affiche-forum.jpg" alt="Une tablée chaleureuse où l'on échange lettres, dessins et photos autour du thé, veillée par une gardienne" className="w-full h-auto block" />
              </div>
            </Reveal>
            <div className="mt-8">
              <GardienBadge gardienId="rose-auguste" label="Gardiens de ce forum" />
            </div>

            {showNew && (
              <motion.form initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
                onSubmit={createThread} className="mt-8 bg-card rounded-3xl border border-border/50 p-6 space-y-4" data-testid="new-thread-form">
                <input required className={inputCls} placeholder="Titre du sujet" data-testid="nt-title" value={nt.title} onChange={(e) => setNt({ ...nt, title: e.target.value })} />
                <div className="flex flex-wrap gap-2" data-testid="nt-category">
                  {CATEGORIES.map((c) => (
                    <button type="button" key={c} onClick={() => setNt({ ...nt, category: c })} className={`rounded-full px-4 py-2 text-sm border transition-colors ${nt.category === c ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/50"}`}>{c}</button>
                  ))}
                </div>
                <textarea required rows={4} className={inputCls} placeholder="Votre message…" data-testid="nt-body" value={nt.body} onChange={(e) => setNt({ ...nt, body: e.target.value })} />
                <button disabled={busy} data-testid="nt-submit" className="rounded-full bg-primary text-primary-foreground px-7 py-3 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Publier</button>
              </motion.form>
            )}

            <div className="flex flex-wrap gap-2 mt-10" data-testid="forum-filters">
              {["Tous", ...CATEGORIES].map((c) => (
                <button key={c} onClick={() => setFilter(c)} data-testid={`forum-filter-${c.toLowerCase()}`} className={`rounded-full px-5 py-2 text-sm border transition-colors ${filter === c ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"}`}>{c}</button>
              ))}
            </div>

            {loading ? (
              <div className="py-20 text-center"><Loader2 className="w-7 h-7 animate-spin text-primary mx-auto" /></div>
            ) : (
              <div className="mt-6 space-y-4">
                {shown.length === 0 && <p className="text-muted-foreground py-10 text-center">Aucun sujet pour le moment. Lancez la première conversation !</p>}
                {shown.map((t) => (
                  <div key={t.id} data-testid={`thread-${t.id}`} className="group bg-card rounded-2xl border border-border/50 p-6 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgb(0,0,0,0.06)] transition-all cursor-pointer" onClick={() => openThread(t.id)}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className={`text-xs px-3 py-1 rounded-full ${catColor[t.category] || "bg-muted"}`}>{t.category}</span>
                      <span className="text-xs text-muted-foreground flex items-center gap-1"><MessageCircle className="w-3.5 h-3.5" /> {t.reply_count} réponse{t.reply_count > 1 ? "s" : ""}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{fmt(t.updated_at)}</span>
                    </div>
                    <h3 className="font-serif-display text-2xl mt-3 group-hover:text-primary transition-colors">{t.title}</h3>
                    <p className="mt-2 text-sm text-foreground/60 line-clamp-2">{t.body}</p>
                    <p className="mt-3 text-xs text-muted-foreground">Par {t.author_name} · {spaceLabel[t.author_space] || "Membre"}</p>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div data-testid="thread-detail">
            <button onClick={() => setActive(null)} data-testid="thread-back-btn" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"><ArrowLeft className="w-4 h-4" /> Retour au forum</button>
            <div className="mt-6 bg-card rounded-3xl border border-border/50 p-7">
              <div className="flex items-center gap-3 flex-wrap">
                <span className={`text-xs px-3 py-1 rounded-full ${catColor[active.thread.category] || "bg-muted"}`}>{active.thread.category}</span>
                <span className="text-xs text-muted-foreground ml-auto">{fmt(active.thread.created_at)}</span>
                {admin && <button onClick={() => delThread(active.thread.id)} data-testid="del-thread-btn" className="text-destructive hover:scale-110 transition-transform"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <h1 className="font-serif-display text-3xl sm:text-4xl mt-4">{active.thread.title}</h1>
              <p className="mt-4 text-foreground/80 leading-relaxed whitespace-pre-wrap">{active.thread.body}</p>
              <p className="mt-4 text-xs text-muted-foreground">Par {active.thread.author_name} · {spaceLabel[active.thread.author_space] || "Membre"}</p>
            </div>

            <div className="mt-6 space-y-3">
              {active.posts.map((p) => (
                <div key={p.id} data-testid={`post-${p.id}`} className="bg-background border border-border/50 rounded-2xl p-5 ml-4">
                  <div className="flex items-start gap-3">
                    <CornerDownRight className="w-4 h-4 text-primary/50 mt-1 shrink-0" />
                    <div className="flex-1">
                      <p className="text-foreground/80 leading-relaxed whitespace-pre-wrap">{p.body}</p>
                      <p className="mt-2 text-xs text-muted-foreground">Par {p.author_name} · {spaceLabel[p.author_space] || "Membre"} · {fmt(p.created_at)}</p>
                    </div>
                    {admin && <button onClick={() => delPost(p.id)} data-testid={`del-post-${p.id}`} className="text-destructive hover:scale-110 transition-transform"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={sendReply} className="mt-6 bg-card rounded-2xl border border-border/50 p-5" data-testid="reply-form">
              <textarea required rows={3} className={inputCls} placeholder="Écrire une réponse bienveillante…" data-testid="reply-body" value={reply} onChange={(e) => setReply(e.target.value)} />
              <button disabled={busy} data-testid="reply-submit" className="mt-3 rounded-full bg-primary text-primary-foreground px-7 py-3 flex items-center gap-2 disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Répondre</button>
            </form>
          </div>
        )}
      </section>
    </div>
  );
};

export default Forum;
